import { produce } from "immer";

import { BASE_SHIPPING_PRICE, FREE_SHIPPING_THRESHOLD } from "@/constants";
import { Cart, CartItem } from "@/hooks/useCart";

export type Item = Required<Pick<CartItem, "id" | "price">>;

const calculateGroupTotal = (group: CartItem[] = []): number =>
  group.reduce((total, { quantity, price }) => {
    return total + quantity * price;
  }, 0);

const getShippingPrice = (itemsTotal: number): number => {
  if (itemsTotal === 0) return 0;
  if (itemsTotal >= FREE_SHIPPING_THRESHOLD) return 0;

  return BASE_SHIPPING_PRICE;
};

/**
 *
 * @param cart Cart object
 * @returns prices for products, dispensers, shipping_price and grand_total
 */
export const calculateCartTotals = (cart: Cart) => {
  const products_price = calculateGroupTotal(cart.products);
  const dispensers_price = calculateGroupTotal(cart.dispensers);
  const itemTotal = products_price + dispensers_price;
  const shipping = getShippingPrice(itemTotal);

  return {
    products_price,
    dispensers_price,
    shipping_price: shipping,
    grand_total: itemTotal + shipping,
  };
};

/* ==== Functions to be refactored / deleted ==== */

export function calculateTotal(cart: Cart) {
  const totalPrice =
    calcItemsPrice(cart?.products) + calcItemsPrice(cart?.dispensers);
  const totalMissingTreshold = Math.max(
    0,
    FREE_SHIPPING_THRESHOLD - totalPrice
  );
  const amountMissing = totalMissingTreshold / 100;
  return { totalPrice, totalMissingTreshold, amountMissing };
}

export function getItemCount(items: CartItem[]): number {
  if (!items) return 0;

  return items.reduce((total, { quantity }) => {
    return total + quantity;
  }, 0);
}

/**
 *
 * @deprecated in favour of calculateGroupTotal
 */
export function calcItemsPrice(items: CartItem[]): number {
  if (!items) return 0;

  return items.reduce((total, { quantity, price }) => {
    return total + quantity * price;
  }, 0);
}

/**
 * @deprecated
 * Merges existing item quantity (if exists)
 * with additional quantity.
 *
 * Additional quantity can be negative
 */
export function combineCartItemQuantity(
  existingItems: CartItem[],
  additionalItem: CartItem
): CartItem {
  const existingItem = (existingItems || []).find(
    ({ id }) => id === additionalItem.id
  );
  return {
    ...additionalItem,
    quantity: Math.min((existingItem?.quantity || 0) + additionalItem.quantity),
  };
}

/**
 * @deprecated
 * Replaces existing item or adds new one.
 */
export function replaceCartItemsItem(
  existingItems: CartItem[] = [],
  updatedItem: CartItem
): CartItem[] {
  return produce(existingItems, (draftItems) => {
    const existingItemIndex = existingItems?.findIndex(
      ({ id }) => updatedItem.id === id
    );
    if (existingItemIndex === -1) {
      draftItems.push(updatedItem);
    } else {
      draftItems[existingItemIndex] = updatedItem;
    }
  });
}
